import React, { useState, useRef, useCallback } from 'react';
import { User, GripVertical, ArrowRightLeft, Check, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const Avatar = ({ employee, size = 40, ringColor = '#4361ee' }) => {
  const initials = employee.name?.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';
  return (
    <div className="flex flex-col items-center">
      <div
        className="rounded-full flex items-center justify-center overflow-hidden border-2"
        style={{ width: size, height: size, borderColor: ringColor, background: '#f1f5f9' }}
      >
        {employee.photo_url ? (
          <img src={`${API}${employee.photo_url}`} alt="" className="w-full h-full object-cover" />
        ) : (
          <span className="text-xs font-bold text-gray-500" style={{ fontSize: size * 0.28 }}>{initials}</span>
        )}
      </div>
    </div>
  );
};

const DraggableNode = ({ employee, bgColor = '#4361ee', ringColor = '#ffd600', size = 40, canDrag, onDragStart }) => {
  const handleDragStart = (e) => {
    if (!canDrag) { e.preventDefault(); return; }
    e.dataTransfer.setData('text/plain', JSON.stringify({
      id: employee.id,
      name: employee.name,
      position: employee.position,
      reporting_manager_ids: employee.reporting_manager_ids || []
    }));
    e.dataTransfer.effectAllowed = 'move';
    if (onDragStart) onDragStart(employee.id);
  };

  return (
    <div
      className={`flex flex-col items-center relative group ${canDrag ? 'cursor-grab active:cursor-grabbing' : ''}`}
      style={{ width: 110 }}
      draggable={canDrag}
      onDragStart={handleDragStart}
      data-testid={`chart-node-${employee.id}`}
    >
      {canDrag && (
        <div className="absolute -top-1 -right-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
          <GripVertical className="h-3.5 w-3.5 text-gray-400" />
        </div>
      )}
      <Avatar employee={employee} size={size} ringColor={ringColor} />
      <div className="mt-1.5 rounded-md px-2 py-1.5 text-center w-full" style={{ background: bgColor }}>
        <p className="text-white text-[10px] font-semibold leading-tight truncate">{employee.name}</p>
        <p className="text-white/70 text-[8px] leading-tight truncate mt-0.5">{employee.position}</p>
      </div>
    </div>
  );
};

const DropTarget = ({ manager, canDrop, onDrop, children, bgColor = '#4361ee', ringColor = '#ffd600', size = 48 }) => {
  const [isOver, setIsOver] = useState(false);

  const handleDragOver = (e) => {
    if (!canDrop) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setIsOver(true);
  };

  const handleDragLeave = () => setIsOver(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsOver(false);
    if (!canDrop) return;
    try {
      const data = JSON.parse(e.dataTransfer.getData('text/plain'));
      if (data.id !== manager.id) {
        onDrop(data, manager);
      }
    } catch { /* ignore parse errors */ }
  };

  return (
    <div
      className={`flex flex-col items-center transition-all duration-200 ${isOver ? 'scale-110' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      data-testid={`drop-target-${manager.id}`}
    >
      <div className={`relative ${isOver ? 'ring-4 ring-blue-300 ring-offset-2 rounded-xl' : ''}`}>
        {children || (
          <div className="flex flex-col items-center" style={{ width: 110 }}>
            <Avatar employee={manager} size={size} ringColor={ringColor} />
            <div className="mt-1.5 rounded-md px-2 py-1.5 text-center w-full" style={{ background: bgColor }}>
              <p className="text-white text-[10px] font-semibold leading-tight truncate">{manager.name}</p>
              <p className="text-white/70 text-[8px] leading-tight truncate mt-0.5">{manager.position}</p>
            </div>
          </div>
        )}
      </div>
      {isOver && (
        <div className="mt-1 text-[9px] text-blue-600 font-medium animate-pulse">Drop here</div>
      )}
    </div>
  );
};

const VLine = ({ height = 30 }) => (
  <div className="flex justify-center">
    <div style={{ width: 1, height, borderLeft: '2px dashed #b0bec5' }} />
  </div>
);

const HBus = ({ children }) => (
  <div className="relative flex justify-center items-start gap-3">
    {React.Children.count(children) > 1 && (
      <div className="absolute top-0 left-1/2 -translate-x-1/2" style={{
        width: 'calc(100% - 110px)',
        height: 1,
        borderTop: '2px dashed #b0bec5',
      }} />
    )}
    {React.Children.map(children, (child) => (
      <div className="flex flex-col items-center">
        <div style={{ width: 1, height: 12, borderLeft: '2px dashed #b0bec5' }} />
        {child}
      </div>
    ))}
  </div>
);

const StaffRow = ({ staff, canDrag, onDragStart }) => (
  <div className="flex flex-wrap justify-center gap-2">
    {staff.map(s => (
      <DraggableNode
        key={s.id}
        employee={s}
        bgColor="#009688"
        ringColor="#26a69a"
        size={36}
        canDrag={canDrag}
        onDragStart={onDragStart}
      />
    ))}
  </div>
);

const LEADERSHIP_ROLES = ['Owner', 'Top Management', 'Management', 'Manager'];
const ROLE_COLORS = {
  Owner: { bg: '#1a1a2e', ring: '#b8860b' },
  'Top Management': { bg: '#1a237e', ring: '#ffd600' },
  Management: { bg: '#7c4dff', ring: '#ff7043' },
  Manager: { bg: '#4361ee', ring: '#ffd600' },
};

const DeptSection = ({ deptName, managers, employees, canDrag, canDrop, onDrop, onDragStart }) => {
  const mgrIds = new Set(managers.map(m => m.manager.id));

  const topMgrs = [];
  const subMgrs = [];
  managers.forEach(entry => {
    const m = entry.manager;
    const rids = m.reporting_manager_ids || [];
    const rid = rids[0];
    if (rid && mgrIds.has(rid)) {
      subMgrs.push(entry);
    } else {
      topMgrs.push(entry);
    }
  });

  const subMgrByParent = {};
  subMgrs.forEach(entry => {
    const pid = (entry.manager.reporting_manager_ids || [])[0];
    if (pid) {
      if (!subMgrByParent[pid]) subMgrByParent[pid] = [];
      subMgrByParent[pid].push(entry);
    }
  });

  if (topMgrs.length === 0) return null;

  const hasAnyContent = topMgrs.some(entry => {
    const mySubs = subMgrByParent[entry.manager.id] || [];
    const subMgrIdSet = new Set(mySubs.map(s => s.manager.id));
    const directStaff = entry.reports.filter(r => !subMgrIdSet.has(r.id));
    return directStaff.length > 0 || mySubs.some(sm => sm.reports.length > 0);
  });
  if (!hasAnyContent) return null;

  return (
    <div className="mb-10">
      <div className="text-center mb-6">
        <h3 className="text-lg font-bold text-gray-800">{deptName}</h3>
        <p className="text-xs text-gray-400">
          Manager &rarr; Staff Reporting Hierarchy
          {canDrag && <span className="ml-2 text-blue-500">(Drag staff to reassign)</span>}
        </p>
      </div>

      {topMgrs.map(entry => {
        const mgr = entry.manager;
        const mySubs = subMgrByParent[mgr.id] || [];
        const subMgrIdSet = new Set(mySubs.map(s => s.manager.id));
        const directStaff = entry.reports.filter(r => !subMgrIdSet.has(r.id));
        const hasContent = directStaff.length > 0 || mySubs.some(sm => sm.reports.length > 0);
        if (!hasContent) return null;

        const mgrColors = ROLE_COLORS[mgr.employee_role] || ROLE_COLORS.Manager;

        return (
          <div key={mgr.id} className="flex flex-col items-center mb-8">
            {/* Tier 1: Top Manager — drop target */}
            {canDrop ? (
              <DropTarget
                manager={mgr}
                canDrop={canDrop}
                onDrop={onDrop}
                bgColor={mgrColors.bg}
                ringColor={mgrColors.ring}
                size={48}
              />
            ) : (
              <div className="flex flex-col items-center" style={{ width: 110 }}>
                <Avatar employee={mgr} size={48} ringColor={mgrColors.ring} />
                <div className="mt-1.5 rounded-md px-2 py-1.5 text-center w-full" style={{ background: mgrColors.bg }}>
                  <p className="text-white text-[10px] font-semibold leading-tight truncate">{mgr.name}</p>
                  <p className="text-white/70 text-[8px] leading-tight truncate mt-0.5">{mgr.position}</p>
                </div>
              </div>
            )}

            {/* Tier 2: Sub-managers — also drop targets */}
            {mySubs.length > 0 && (
              <>
                <VLine height={25} />
                <HBus>
                  {mySubs.map(sm => (
                    canDrop ? (
                      <DropTarget
                        key={sm.manager.id}
                        manager={sm.manager}
                        canDrop={canDrop}
                        onDrop={onDrop}
                        bgColor="#7c4dff"
                        ringColor="#ff7043"
                        size={42}
                      />
                    ) : (
                      <div key={sm.manager.id} className="flex flex-col items-center" style={{ width: 110 }}>
                        <Avatar employee={sm.manager} size={42} ringColor="#ff7043" />
                        <div className="mt-1.5 rounded-md px-2 py-1.5 text-center w-full" style={{ background: '#7c4dff' }}>
                          <p className="text-white text-[10px] font-semibold leading-tight truncate">{sm.manager.name}</p>
                          <p className="text-white/70 text-[8px] leading-tight truncate mt-0.5">{sm.manager.position}</p>
                        </div>
                      </div>
                    )
                  ))}
                </HBus>
              </>
            )}

            {/* Tier 3: Staff rows per sub-manager — draggable */}
            {mySubs.map(sm => {
              if (sm.reports.length === 0) return null;
              const rows = [];
              for (let i = 0; i < sm.reports.length; i += 6) {
                rows.push(sm.reports.slice(i, i + 6));
              }
              return (
                <div key={sm.manager.id} className="mt-1">
                  <VLine height={20} />
                  {rows.map((row, ri) => (
                    <div key={ri}>
                      {ri > 0 && <VLine height={15} />}
                      <StaffRow staff={row} canDrag={canDrag} onDragStart={onDragStart} />
                    </div>
                  ))}
                </div>
              );
            })}

            {/* Direct staff — draggable */}
            {directStaff.length > 0 && (
              <div className="mt-1">
                <VLine height={20} />
                {(() => {
                  const rows = [];
                  for (let i = 0; i < directStaff.length; i += 6) {
                    rows.push(directStaff.slice(i, i + 6));
                  }
                  return rows.map((row, ri) => (
                    <div key={ri}>
                      {ri > 0 && <VLine height={15} />}
                      <StaffRow staff={row} canDrag={canDrag} onDragStart={onDragStart} />
                    </div>
                  ));
                })()}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const DEPT_ORDER = [
  'TOP MANAGEMENT', 'MANAGEMENT', 'FINANCE', 'IT', 'HUMAN RESOURCE',
  'FOH', 'BAR', 'KITCHEN', 'OPERATIONS', 'PRO',
];

const OrgChartViewer = ({ hierarchy, employees, isSuperAdmin = false, onReassign }) => {
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [saving, setSaving] = useState(false);

  const canDrag = isSuperAdmin;
  const canDrop = isSuperAdmin;

  const handleDrop = useCallback((draggedEmp, targetManager) => {
    const currentManagerIds = draggedEmp.reporting_manager_ids || [];
    const hasExistingManagers = currentManagerIds.length > 0;
    const alreadyReports = currentManagerIds.includes(targetManager.id);

    if (alreadyReports) return;

    setConfirmDialog({
      employee: draggedEmp,
      manager: targetManager,
      hasExistingManagers,
      currentManagerIds
    });
  }, []);

  const handleConfirm = async (mode) => {
    if (!confirmDialog || !onReassign) return;
    setSaving(true);
    try {
      await onReassign(confirmDialog.employee.id, confirmDialog.manager.id, mode);
    } finally {
      setSaving(false);
      setConfirmDialog(null);
    }
  };

  const orderedDepts = DEPT_ORDER.filter(d => d in hierarchy);
  Object.keys(hierarchy).sort().forEach(d => {
    if (!orderedDepts.includes(d)) orderedDepts.push(d);
  });

  return (
    <div className="space-y-2" data-testid="org-chart-viewer">
      {canDrag && (
        <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-lg mb-4">
          <ArrowRightLeft className="h-4 w-4 text-blue-600" />
          <p className="text-xs text-blue-700 font-medium" data-testid="drag-drop-hint">
            Drag any staff member and drop onto a manager to reassign their reporting line
          </p>
        </div>
      )}

      {orderedDepts.map(dept => {
        const data = hierarchy[dept];
        if (!data) return null;
        return (
          <DeptSection
            key={dept}
            deptName={dept}
            managers={data.managers}
            employees={employees}
            canDrag={canDrag}
            canDrop={canDrop}
            onDrop={handleDrop}
          />
        );
      })}

      {/* Reassignment Confirmation Dialog */}
      <Dialog open={!!confirmDialog} onOpenChange={() => !saving && setConfirmDialog(null)}>
        <DialogContent className="sm:max-w-md" data-testid="reassign-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5 text-blue-600" />
              Reassign Employee
            </DialogTitle>
          </DialogHeader>
          {confirmDialog && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="text-center flex-1">
                    <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-2">
                      <User className="h-6 w-6 text-teal-600" />
                    </div>
                    <p className="font-semibold text-sm">{confirmDialog.employee.name}</p>
                    <p className="text-xs text-gray-500">{confirmDialog.employee.position}</p>
                  </div>
                  <div className="px-4">
                    <ArrowRightLeft className="h-5 w-5 text-gray-400" />
                  </div>
                  <div className="text-center flex-1">
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-2">
                      <User className="h-6 w-6 text-blue-600" />
                    </div>
                    <p className="font-semibold text-sm">{confirmDialog.manager.name}</p>
                    <p className="text-xs text-gray-500">{confirmDialog.manager.position}</p>
                  </div>
                </div>
              </div>

              {confirmDialog.hasExistingManagers && confirmDialog.currentManagerIds.length < 3 ? (
                <div className="space-y-2">
                  <p className="text-sm text-gray-600">
                    This employee already has {confirmDialog.currentManagerIds.length} reporting manager(s).
                    How would you like to proceed?
                  </p>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleConfirm('replace')}
                      disabled={saving}
                      variant="default"
                      className="flex-1"
                      data-testid="reassign-replace-btn"
                    >
                      <Check className="h-4 w-4 mr-1" />
                      {saving ? 'Saving...' : 'Replace All'}
                    </Button>
                    <Button
                      onClick={() => handleConfirm('add')}
                      disabled={saving}
                      variant="outline"
                      className="flex-1"
                      data-testid="reassign-add-btn"
                    >
                      <Check className="h-4 w-4 mr-1" />
                      {saving ? 'Saving...' : 'Add Manager'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-gray-600">
                    {confirmDialog.hasExistingManagers
                      ? `This employee already has 3 managers. The new manager will replace all existing ones.`
                      : `Assign ${confirmDialog.manager.name} as the reporting manager?`
                    }
                  </p>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleConfirm('replace')}
                      disabled={saving}
                      className="flex-1"
                      data-testid="reassign-confirm-btn"
                    >
                      <Check className="h-4 w-4 mr-1" />
                      {saving ? 'Saving...' : 'Confirm'}
                    </Button>
                    <Button
                      onClick={() => setConfirmDialog(null)}
                      disabled={saving}
                      variant="outline"
                      className="flex-1"
                      data-testid="reassign-cancel-btn"
                    >
                      <X className="h-4 w-4 mr-1" /> Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrgChartViewer;
